import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Play, Pause, RotateCcw, AlertTriangle, Shield, Target } from 'lucide-react';

const THREAT_TYPES = [
  { name: 'FPV Kamikaze', type: 'air', color: 0xef4444, speed: 0.12, threatLevel: 'critical', softKillChance: 0.4 },
  { name: 'Recon Quadcopter', type: 'air', color: 0xf59e0b, speed: 0.08, threatLevel: 'medium', softKillChance: 0.85 },
  { name: 'Loitering Munition', type: 'air', color: 0xef4444, speed: 0.10, threatLevel: 'high', softKillChance: 0.3 },
  { name: 'Surface USV', type: 'surface', color: 0x22c55e, speed: 0.06, threatLevel: 'high', softKillChance: 0.5 },
  { name: 'Subsurface UUV', type: 'underwater', color: 0x3b82f6, speed: 0.04, threatLevel: 'medium', softKillChance: 0.6 },
  { name: 'Surveillance Fixed-Wing', type: 'air', color: 0xf59e0b, speed: 0.09, threatLevel: 'low', softKillChance: 0.9 },
  { name: 'Armed MALE UAV', type: 'air', color: 0xef4444, speed: 0.07, threatLevel: 'critical', softKillChance: 0.2 },
  { name: 'Swarm Element', type: 'air', color: 0xff6b6b, speed: 0.11, threatLevel: 'high', softKillChance: 0.35 },
];

const SOFT_KILL_METHODS = [
  { id: 'rf_jamming', label: 'RF Jamming', color: '#eab308' },
  { id: 'gps_spoofing', label: 'GPS Spoofing', color: '#3b82f6' },
  { id: 'comm_disruption', label: 'Comm Disruption', color: '#f97316' },
  { id: 'cyber_takeover', label: 'Cyber Takeover', color: '#a855f7' },
];

const HARD_KILL_OPTIONS = [
  { id: 'directed_energy', label: 'Directed Energy Weapon (DEW)', color: '#22c55e', effectColor: 0x00ff00 },
  { id: 'anti_drone_gun', label: 'Anti Drone Gun', color: '#eab308', effectColor: 0xffcc00 },
  { id: 'kinetic_interceptor', label: 'Kinetic Interceptor', color: '#f97316', effectColor: 0xff6600 },
  { id: 'net_capture', label: 'Net Capture', color: '#06b6d4', effectColor: 0x00ffff },
  { id: 'anti_drone_missile', label: 'Anti-Drone Missile', color: '#ef4444', effectColor: 0xff0000 },
];

export default function SimulationScenario2() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationRef = useRef(null);

  // All mutable simulation state lives in refs (no closure stale-state)
  const playingRef = useRef(false);
  const threatsRef = useRef([]);
  const spawnTimerRef = useRef(0);
  const threatCounterRef = useRef(0);
  const statsRef = useRef({ detected: 0, softKilled: 0, hardKilled: 0, breached: 0 });
  const logsRef = useRef([]);
  const hardKillPromptRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [logs, setLogs] = useState([]);
  const [activeThreats, setActiveThreats] = useState([]);
  const [hardKillPrompt, setHardKillPrompt] = useState(null);
  const [stats, setStats] = useState({ detected: 0, softKilled: 0, hardKilled: 0, breached: 0 });

  const pushLog = useCallback((message, type = 'info') => {
    const entry = { timestamp: new Date().toLocaleTimeString(), message, type };
    logsRef.current = [...logsRef.current.slice(-30), entry];
    setLogs([...logsRef.current]);
  }, []);

  const bumpStat = useCallback((field) => {
    statsRef.current = { ...statsRef.current, [field]: statsRef.current[field] + 1 };
    setStats({ ...statsRef.current });
  }, []);

  const syncActiveThreats = useCallback(() => {
    const list = threatsRef.current
      .filter(t => t.visible && t.userData.status !== 'neutralized' && t.userData.status !== 'destroyed')
      .map(t => ({
        id: t.userData.id, name: t.userData.name, type: t.userData.type,
        status: t.userData.status, threatLevel: t.userData.threatLevel,
        distance: t.position.distanceTo(new THREE.Vector3(0, 0, 0)).toFixed(1)
      }));
    setActiveThreats(list);
  }, []);

  const togglePlay = useCallback(() => {
    playingRef.current = !playingRef.current;
    setIsPlaying(playingRef.current);
    if (playingRef.current && logsRef.current.length === 0) {
      pushLog('Scenario 2 started — Awaiting contacts', 'info');
    }
  }, [pushLog]);

  // Refs for callbacks (animation loop uses these)
  const cbRef = useRef({ pushLog, bumpStat, syncActiveThreats, setHardKillPrompt });
  useEffect(() => {
    cbRef.current = { pushLog, bumpStat, syncActiveThreats, setHardKillPrompt };
  }, [pushLog, bumpStat, syncActiveThreats]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Reset sim state on mount
    playingRef.current = false;
    threatsRef.current = [];
    spawnTimerRef.current = 0;
    threatCounterRef.current = 0;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020617);
    scene.fog = new THREE.FogExp2(0x020617, 0.006);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(55, container.clientWidth / container.clientHeight, 0.1, 1000);
    camera.position.set(40, 30, 40);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 15;
    controls.maxDistance = 120;

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(10, 20, 10);
    scene.add(dirLight);

    // Ocean
    const ocean = new THREE.Mesh(
      new THREE.PlaneGeometry(300, 300),
      new THREE.MeshStandardMaterial({ color: 0x0c4a6e, transparent: true, opacity: 0.35, side: THREE.DoubleSide })
    );
    ocean.rotation.x = -Math.PI / 2;
    ocean.position.y = -1;
    scene.add(ocean);

    const grid = new THREE.GridHelper(200, 40, 0x1e3a5f, 0x0f2847);
    grid.position.y = -0.9;
    scene.add(grid);

    // Ship
    const shipGroup = new THREE.Group();
    const s = 0.5;
    const hull = new THREE.Mesh(new THREE.BoxGeometry(2*s, 1*s, 6*s), new THREE.MeshStandardMaterial({ color: 0xfbbf24, metalness: 0.5, roughness: 0.3 }));
    hull.position.set(0, -0.3*s, 0);
    shipGroup.add(hull);
    const deck = new THREE.Mesh(new THREE.BoxGeometry(1.8*s, 0.2*s, 5.5*s), new THREE.MeshStandardMaterial({ color: 0xf59e0b }));
    deck.position.set(0, 0.2*s, 0);
    shipGroup.add(deck);
    const bridge = new THREE.Mesh(new THREE.BoxGeometry(1.2*s, 1*s, 1.5*s), new THREE.MeshStandardMaterial({ color: 0xeab308 }));
    bridge.position.set(0, 0.8*s, -0.8*s);
    shipGroup.add(bridge);
    const dome = new THREE.Mesh(new THREE.SphereGeometry(0.2*s, 16, 16), new THREE.MeshStandardMaterial({ color: 0x0ea5e9, emissive: 0x0ea5e9, emissiveIntensity: 0.5 }));
    dome.position.set(0, 2.1*s, -0.8*s);
    shipGroup.add(dome);
    scene.add(shipGroup);

    // Protection zones
    const addZone = (radius, color, opacity) => {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(radius, 32, 32),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity, wireframe: true, side: THREE.DoubleSide })
      );
      sphere.userData.isZone = true;
      scene.add(sphere);
    };
    addZone(15, 0x0ea5e9, 0.06);
    addZone(6, 0xeab308, 0.08);
    addZone(3, 0xef4444, 0.10);

    [15, 6, 3].forEach(r => {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(r - 0.05, r + 0.05, 64),
        new THREE.MeshBasicMaterial({ color: 0x1e3a5f, side: THREE.DoubleSide })
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -0.8;
      scene.add(ring);
    });

    function createThreatMesh(type, color) {
      let geo;
      if (type === 'air') geo = new THREE.OctahedronGeometry(0.7, 0);
      else if (type === 'surface') geo = new THREE.BoxGeometry(0.9, 0.4, 0.7);
      else geo = new THREE.SphereGeometry(0.5, 16, 16);
      const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.6 }));
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.8, 0.9, 32),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6, side: THREE.DoubleSide })
      );
      ring.rotation.x = Math.PI / 2;
      mesh.add(ring);
      return mesh;
    }

    function spawnThreat() {
      const template = THREAT_TYPES[Math.floor(Math.random() * THREAT_TYPES.length)];
      const angle = Math.random() * Math.PI * 2;
      const dist = 22 + Math.random() * 8;
      const yMap = { air: 6 + Math.random() * 6, surface: 0.5, underwater: -3 - Math.random() * 3 };
      const startPos = new THREE.Vector3(Math.cos(angle) * dist, yMap[template.type], Math.sin(angle) * dist);

      threatCounterRef.current += 1;
      const id = `T-${String(threatCounterRef.current).padStart(3, '0')}`;

      const mesh = createThreatMesh(template.type, template.color);
      mesh.position.copy(startPos);
      mesh.userData = {
        id, name: template.name, type: template.type, color: template.color,
        speed: template.speed * (0.8 + Math.random() * 0.4),
        threatLevel: template.threatLevel, softKillChance: template.softKillChance,
        status: 'approaching', detected: false, tracking: false,
        softKillApplied: false, hardKillNeeded: false, hardKillAuthorized: false,
      };
      scene.add(mesh);
      threatsRef.current.push(mesh);
      cbRef.current.pushLog(`[CONTACT] ${id} ${template.name} inbound — bearing ${(angle * 180 / Math.PI).toFixed(0)}°`, 'info');
      cbRef.current.syncActiveThreats();
    }

    function updateThreats() {
      const ship = new THREE.Vector3(0, 0, 0);
      let stateChanged = false;

      threatsRef.current.forEach(threat => {
        if (!threat.visible) return;
        const ud = threat.userData;
        if (ud.status === 'neutralized' || ud.status === 'destroyed') return;

        if (ud.status === 'soft_killed') {
          threat.position.y -= 0.08;
          threat.rotation.z += 0.06;
          if (threat.position.y < -5) { threat.visible = false; ud.status = 'neutralized'; stateChanged = true; }
          return;
        }
        if (ud.status === 'hard_kill_active') {
          threat.scale.multiplyScalar(0.92);
          if (threat.scale.x < 0.1) { threat.visible = false; ud.status = 'destroyed'; stateChanged = true; }
          return;
        }
        if (ud.status === 'awaiting_hard_kill') {
          const pulse = 1 + Math.sin(Date.now() * 0.01) * 0.3;
          threat.scale.setScalar(pulse);
          return;
        }

        // Move toward ship
        const dist = threat.position.distanceTo(ship);
        const dir = new THREE.Vector3().subVectors(ship, threat.position).normalize();
        const targetY = ud.type === 'air' ? 4 : ud.type === 'underwater' ? -3 : 0.5;
        dir.y = (targetY - threat.position.y) * 0.02;
        threat.position.add(dir.multiplyScalar(ud.speed));

        const sc = 1 + Math.sin(Date.now() * 0.004) * 0.12;
        threat.scale.setScalar(sc);

        if (dist < 16 && !ud.detected) {
          ud.detected = true;
          ud.status = 'detected';
          cbRef.current.pushLog(`[5KM] ${ud.id} ${ud.name} DETECTED — ${ud.threatLevel.toUpperCase()} threat, ${ud.type} domain`, 'warning');
          cbRef.current.bumpStat('detected');
          stateChanged = true;
        }
        if (dist < 13 && ud.detected && !ud.tracking) {
          ud.tracking = true;
          ud.status = 'tracking';
          cbRef.current.pushLog(`[TRACK] ${ud.id} Sensors locked — Range: ${(dist * 0.333).toFixed(1)}km, Closing`, 'info');
          stateChanged = true;
        }
        if (dist < 7 && !ud.softKillApplied) {
          ud.softKillApplied = true;
          const softKill = SOFT_KILL_METHODS[Math.floor(Math.random() * SOFT_KILL_METHODS.length)];
          cbRef.current.pushLog(`[2KM] ${ud.id} AUTO SOFT KILL: ${softKill.label} deployed`, 'danger');

          for (let i = 0; i < 3; i++) {
            const wave = new THREE.Mesh(
              new THREE.RingGeometry(0.3 + i * 0.6, 0.5 + i * 0.6, 32),
              new THREE.MeshBasicMaterial({ color: parseInt(softKill.color.replace('#', '0x')), transparent: true, opacity: 0.7, side: THREE.DoubleSide })
            );
            wave.position.copy(threat.position);
            wave.lookAt(ship);
            wave.userData.isEffect = true;
            wave.userData.life = 1.0;
            scene.add(wave);
          }

          const success = Math.random() < ud.softKillChance;
          setTimeout(() => {
            if (success) {
              ud.status = 'soft_killed';
              cbRef.current.pushLog(`[RESULT] ${ud.id} SOFT KILL SUCCESS — ${softKill.label} effective, target falling`, 'success');
              cbRef.current.bumpStat('softKilled');
            } else {
              ud.status = 'awaiting_hard_kill';
              ud.hardKillNeeded = true;
              cbRef.current.pushLog(`[ALERT] ${ud.id} SOFT KILL FAILED — ${ud.name} resistant! OPERATOR: Authorize Hard Kill`, 'danger');
              const prompt = {
                threatId: ud.id, name: ud.name, type: ud.type,
                threatLevel: ud.threatLevel, distance: (dist * 0.333).toFixed(1)
              };
              hardKillPromptRef.current = prompt;
              cbRef.current.setHardKillPrompt(prompt);
            }
            cbRef.current.syncActiveThreats();
          }, 800);
          stateChanged = true;
        }

        if (dist < 3.5 && ud.status !== 'soft_killed' && ud.status !== 'awaiting_hard_kill' && ud.status !== 'hard_kill_active') {
          ud.status = 'breached';
          threat.visible = false;
          cbRef.current.pushLog(`[BREACH] ${ud.id} ${ud.name} BREACHED 1KM PERIMETER!`, 'danger');
          cbRef.current.bumpStat('breached');
          stateChanged = true;
        }
      });

      // Effects cleanup
      const toRemove = [];
      scene.children.forEach(c => {
        if (c.userData.isEffect) {
          c.userData.life -= 0.025;
          if (c.material) c.material.opacity = Math.max(c.userData.life, 0);
          if (c.userData.isExplosion) c.scale.setScalar(1 + (1 - c.userData.life) * 4);
          if (c.userData.life <= 0) toRemove.push(c);
        }
      });
      toRemove.forEach(o => scene.remove(o));

      if (stateChanged) cbRef.current.syncActiveThreats();
    }

    // Render + game loop combined (RAF guarantees execution)
    let frameCount = 0;
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      frameCount++;

      // Game logic — runs only when playing
      if (playingRef.current) {
        spawnTimerRef.current += 1;
        const activeCount = threatsRef.current.filter(t =>
          t.visible && (t.userData.status === 'approaching' || t.userData.status === 'detected' || t.userData.status === 'tracking')
        ).length;
        if (spawnTimerRef.current > 75 && activeCount < 4) {
          spawnTimerRef.current = 0;
          try { spawnThreat(); } catch (e) { console.error('spawnThreat error:', e); }
        }
        try { updateThreats(); } catch (e) { console.error('updateThreats error:', e); }
        if (frameCount % 15 === 0) cbRef.current.syncActiveThreats();
      }

      // Visuals
      scene.children.forEach(c => { if (c.userData.isZone) c.rotation.y += 0.0008; });
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      playingRef.current = false;
      if (container && renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHardKill = (optionId) => {
    const prompt = hardKillPromptRef.current;
    if (!prompt) return;
    const option = HARD_KILL_OPTIONS.find(o => o.id === optionId);
    const threat = threatsRef.current.find(t => t.userData.id === prompt.threatId);

    if (threat && option && sceneRef.current) {
      pushLog(`[HARD KILL] ${threat.userData.id} AUTHORIZED: ${option.label} by Operator`, 'danger');

      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 1, 0), threat.position.clone()]),
        new THREE.LineBasicMaterial({ color: option.effectColor, linewidth: 3 })
      );
      line.userData.isEffect = true;
      line.userData.life = 1.0;
      sceneRef.current.add(line);

      const explosion = new THREE.Mesh(
        new THREE.SphereGeometry(1.5, 16, 16),
        new THREE.MeshBasicMaterial({ color: option.effectColor, transparent: true, opacity: 0.9 })
      );
      explosion.position.copy(threat.position);
      explosion.userData.isEffect = true;
      explosion.userData.isExplosion = true;
      explosion.userData.life = 1.0;
      sceneRef.current.add(explosion);

      setTimeout(() => {
        threat.userData.status = 'hard_kill_active';
        threat.userData.hardKillAuthorized = true;
        bumpStat('hardKilled');
        pushLog(`[RESULT] ${threat.userData.id} DESTROYED by ${option.label}`, 'success');
        syncActiveThreats();
      }, 300);
    }
    hardKillPromptRef.current = null;
    setHardKillPrompt(null);
  };

  const dismissHardKill = () => {
    const prompt = hardKillPromptRef.current;
    if (!prompt) return;
    const threat = threatsRef.current.find(t => t.userData.id === prompt.threatId);
    if (threat) {
      threat.userData.status = 'approaching';
      threat.userData.softKillApplied = false;
      pushLog(`[OPERATOR] ${prompt.threatId} Hard kill declined — threat resuming approach`, 'warning');
      syncActiveThreats();
    }
    hardKillPromptRef.current = null;
    setHardKillPrompt(null);
  };

  const resetSimulation = () => {
    playingRef.current = false;
    setIsPlaying(false);
    logsRef.current = [];
    setLogs([]);
    setActiveThreats([]);
    hardKillPromptRef.current = null;
    setHardKillPrompt(null);
    statsRef.current = { detected: 0, softKilled: 0, hardKilled: 0, breached: 0 };
    setStats({ detected: 0, softKilled: 0, hardKilled: 0, breached: 0 });
    spawnTimerRef.current = 0;
    threatCounterRef.current = 0;

    if (sceneRef.current) {
      const toRemove = [];
      sceneRef.current.children.forEach(c => {
        if (c.userData.isEffect || c.userData.isExplosion) toRemove.push(c);
      });
      threatsRef.current.forEach(t => toRemove.push(t));
      toRemove.forEach(o => sceneRef.current.remove(o));
      threatsRef.current = [];
    }
    pushLog('Scenario 2 reset — Ready', 'info');
  };

  const getStatusColor = (s) => {
    const m = { approaching: 'text-zinc-400', detected: 'text-yellow-500', tracking: 'text-orange-400',
      awaiting_hard_kill: 'text-red-500 animate-pulse', soft_killed: 'text-green-400', destroyed: 'text-green-500',
      breached: 'text-red-600', neutralized: 'text-green-500' };
    return m[s] || 'text-zinc-400';
  };

  const getLogColor = (t) => {
    const m = { warning: 'text-yellow-400', danger: 'text-red-400', success: 'text-green-400' };
    return m[t] || 'text-zinc-400';
  };

  const threatLevelColor = (l) => {
    const m = { critical: 'text-red-500', high: 'text-orange-500', medium: 'text-yellow-500', low: 'text-green-500' };
    return m[l] || 'text-zinc-400';
  };

  return (
    <div className="h-full flex flex-col bg-zinc-950" data-testid="simulation-scenario2">
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative">
          <div ref={containerRef} className="w-full h-full" />

          <div className="absolute top-4 left-4 flex gap-2" data-testid="sim2-stats">
            {[
              { label: 'Detected', val: stats.detected, color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' },
              { label: 'Soft Kill', val: stats.softKilled, color: 'text-green-400 border-green-500/30 bg-green-500/10' },
              { label: 'Hard Kill', val: stats.hardKilled, color: 'text-red-400 border-red-500/30 bg-red-500/10' },
              { label: 'Breached', val: stats.breached, color: 'text-red-600 border-red-600/30 bg-red-600/10' },
            ].map(st => (
              <div key={st.label} className={`px-3 py-2 rounded border backdrop-blur-md text-center ${st.color}`}>
                <div className="text-lg font-bold font-mono">{st.val}</div>
                <div className="text-[10px] uppercase">{st.label}</div>
              </div>
            ))}
          </div>

          <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md border border-zinc-800 p-3 rounded-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 text-sky-500 flex items-center gap-1">
              <Shield size={14} /> Defense Layers
            </h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-sky-500" /><span className="text-zinc-400">5km — Detect &amp; Track</span></div>
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-yellow-500" /><span className="text-zinc-400">2km — Auto Soft Kill</span></div>
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-zinc-400">1km — Hard Kill Zone</span></div>
            </div>
          </div>

          {hardKillPrompt && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-20" data-testid="hard-kill-modal">
              <div className="bg-zinc-900 border-2 border-red-500 rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex items-center gap-2 text-red-500 mb-4">
                  <AlertTriangle size={24} />
                  <h3 className="text-lg font-bold uppercase">Hard Kill Authorization Required</h3>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded p-3 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-zinc-500">Target:</span> <span className="text-white font-mono">{hardKillPrompt.threatId}</span></div>
                    <div><span className="text-zinc-500">Type:</span> <span className="text-white">{hardKillPrompt.name}</span></div>
                    <div><span className="text-zinc-500">Threat:</span> <span className={`font-bold uppercase ${threatLevelColor(hardKillPrompt.threatLevel)}`}>{hardKillPrompt.threatLevel}</span></div>
                    <div><span className="text-zinc-500">Range:</span> <span className="text-white font-mono">{hardKillPrompt.distance}km</span></div>
                  </div>
                  <p className="text-xs text-red-400 mt-2">Soft kill countermeasure failed. Select hard kill option:</p>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {HARD_KILL_OPTIONS.map(opt => (
                    <button key={opt.id} onClick={() => handleHardKill(opt.id)}
                      className="p-3 rounded border border-zinc-700 hover:border-red-500 bg-zinc-800 hover:bg-red-500/10 transition-all text-left"
                      data-testid={`hardkill-${opt.id}`}>
                      <div className="font-bold text-xs" style={{ color: opt.color }}>{opt.label}</div>
                    </button>
                  ))}
                </div>
                <button onClick={dismissHardKill}
                  className="w-full py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-xs font-mono uppercase text-zinc-400 transition-colors"
                  data-testid="decline-hardkill">
                  Decline — Continue Tracking
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="w-80 bg-zinc-900/50 border-l border-zinc-800 flex flex-col">
          <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
            <button onClick={togglePlay}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded font-mono text-xs uppercase tracking-wider transition-all ${
                isPlaying ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' : 'bg-green-500/20 text-green-400 border border-green-500/50'
              }`} data-testid="s2-play-btn">
              {isPlaying ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Play</>}
            </button>
            <button onClick={resetSimulation}
              className="flex items-center gap-1 px-3 py-2 rounded font-mono text-xs uppercase bg-zinc-700/50 text-zinc-300 border border-zinc-600 hover:bg-zinc-700 transition-all"
              data-testid="s2-reset-btn">
              <RotateCcw size={14} /> Reset
            </button>
          </div>

          <div className="p-3 border-b border-zinc-800">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Target size={14} className="text-red-500" /> Active Threats
              <span className="ml-auto text-zinc-500 font-mono">{activeThreats.length}</span>
            </h3>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {activeThreats.length === 0 ? (
                <p className="text-xs text-zinc-500">No active threats{isPlaying ? ' — waiting for contacts...' : ''}</p>
              ) : (
                activeThreats.map(t => (
                  <div key={t.id} className="flex items-center justify-between text-xs bg-zinc-800/50 rounded px-2 py-1.5" data-testid={`threat-${t.id}`}>
                    <div>
                      <span className="text-white font-mono">{t.id}</span>
                      <span className="text-zinc-500 ml-1.5">{t.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500 font-mono">{(parseFloat(t.distance) * 0.333).toFixed(1)}km</span>
                      <span className={`font-mono uppercase text-[10px] ${getStatusColor(t.status)}`}>{t.status.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex-1 p-3 flex flex-col min-h-0">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2">Event Log</h3>
            <div className="flex-1 overflow-y-auto space-y-0.5 font-mono text-[11px] bg-black/30 rounded p-2" data-testid="event-log">
              {logs.length === 0 ? (
                <p className="text-zinc-500">Press PLAY to begin...</p>
              ) : (
                logs.map((l, i) => (
                  <div key={i} className={getLogColor(l.type)}>
                    <span className="text-zinc-600">[{l.timestamp}]</span> {l.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
